import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Card,
  Table,
  Space,
  Typography,
  Tag,
  Button,
  Input,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  message,
  Spin
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import VMSInvoiceService from '../../../services/VMSInvoiceService';
import { classifyError, vmsErrorText } from './VMSInvoiceErrors';
import {
  getBatchDisplayStatus,
  getStatusColor,
  getStatusIcon,
  getStatusLabel
} from './VMSInvoiceStatus';
import PrefixPath from '../../../config/AppConfig';
import Headers from '../../../redux/Actions/Headers/Header';
import './BatchList.scss';
import Utils from '../../../utils/Utils';
import CommonFunctions from '../../../utils/CommonFunction/CommonFunctions';
import ResponsiveGrid from 'Components/TableMui/ResponsiveGrid';
import ContentCard from 'Components/Card/Card';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const BatchList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [allBatches, setAllBatches] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [loadingInvoices, setLoadingInvoices] = useState({});
  const [filters, setFilters] = useState({
    invoiceNumber: '',
    status: '',
    dateRange: null
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  // Refs to read the latest values inside table/pagination callbacks without stale closures
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const pageSizeRef = useRef(pagination.pageSize);
  pageSizeRef.current = pagination.pageSize;
  // Latest expanded ids, read inside the detail-panel change handler
  const expandedRowKeysRef = useRef(expandedRowKeys);
  expandedRowKeysRef.current = expandedRowKeys;

  // Page header (title bar) shown by the app layout
  useEffect(() => {
    dispatch(Headers.setHeader({ title: 'VMS Invoice Batches' }));
    return () => dispatch(Headers.resetHeader());
  }, [dispatch]);

  const fetchBatches = useCallback(async (page, size, activeFilters) => {
    try {
      setLoading(true);

      const params = {
        pageNumber: page,
        pageSize: size,
        search: activeFilters.invoiceNumber || undefined,
        batchStatus: activeFilters.status || undefined
      };
      if (activeFilters.dateRange && activeFilters.dateRange[0] && activeFilters.dateRange[1]) {
        params.startDate = activeFilters.dateRange[0].startOf('day').toISOString();
        params.endDate = activeFilters.dateRange[1].endOf('day').toISOString();
      }

      const response = await VMSInvoiceService.searchInvoices(params);

      if (response.data.success) {
        setAllBatches(response.data.batches || []);
        setPagination({
          current: response.data.pageNumber || page,
          pageSize: response.data.pageSize || size,
          total: response.data.totalBatches || 0
        });
      }

      setLoading(false);
    } catch (error) {
      const reason = classifyError(error);
      if (reason === 'auth') {
        setLoading(false);
        return;
      }

      message.error(vmsErrorText(reason, {
        timeout: 'The request timed out. Please try again or contact support if the problem persists.',
        fallback: 'Unable to load batches. Please try again.'
      }));

      console.error('Fetch batches error:', error.response?.data?.message || error.message);
      setLoading(false);
    }
  }, []);

  // Debounced fetch whenever filters change (also handles the initial load).
  // Filter changes always reset to page 1; pageSize is preserved.
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchBatches(1, pageSizeRef.current, filters);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters, fetchBatches]);

  // Memoized statistics for the current page - only recalculate when allBatches change
  const statistics = useMemo(() => {
    const stats = {
      pendingBatches: 0,
      completedBatches: 0,
      failedBatches: 0,
      partialSuccessBatches: 0
    };

    allBatches.forEach(batch => {
      const status = getBatchDisplayStatus(batch)?.toLowerCase();

      if (status === 'partial success') {
        stats.partialSuccessBatches++;
      } else if (status === 'pre' || status === 'pending' || status === 'processing') {
        stats.pendingBatches++;
      } else if (status === 'success' || status === 'completed') {
        stats.completedBatches++;
      } else if (status === 'failed' || status === 'validationfailed' || status === 'error') {
        stats.failedBatches++;
      }
    });

    return stats;
  }, [allBatches]);

  // Lazy-load invoices for a batch (only fetches once per batch)
  const loadInvoices = useCallback(async (batchId) => {
    if (expandedInvoices[batchId]) return;
    setLoadingInvoices(prev => ({ ...prev, [batchId]: true }));

    try {
      const response = await VMSInvoiceService.getInvoicesByBatch(batchId);
      if (response.data.success) {
        setExpandedInvoices(prev => ({
          ...prev,
          [batchId]: response.data.invoices || []
        }));
      }
    } catch (error) {
      console.error('Error loading invoices for batch', batchId, error);
      if (classifyError(error) !== 'auth') {
        message.error('Unable to load invoices for this batch.');
      }
    } finally {
      setLoadingInvoices(prev => ({ ...prev, [batchId]: false }));
    }
  }, [expandedInvoices]);

  // DataGridPro detail-panel expand/collapse (v7 passes an array of ids).
  // Load invoices for any newly-expanded batch.
  const handleDetailPanelChange = useCallback((newIds) => {
    const added = newIds.filter(id => !expandedRowKeysRef.current.includes(id));
    setExpandedRowKeys(newIds);
    added.forEach(id => loadInvoices(id));
  }, [loadInvoices]);

  const handleInvoiceNumberChange = useCallback((e) => {
    setFilters(prev => ({ ...prev, invoiceNumber: e.target.value }));
  }, []);

  const handleStatusChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, status: value || '' }));
  }, []);

  const handleDateChange = useCallback((dates) => {
    setFilters(prev => ({ ...prev, dateRange: dates }));
  }, []);

  const handleReset = useCallback(() => {
    setFilters({
      invoiceNumber: '',
      status: '',
      dateRange: null
    });
  }, []);

  const handleRefresh = useCallback(() => {
    setExpandedInvoices({});
    setExpandedRowKeys([]);
    fetchBatches(pagination.current, pagination.pageSize, filtersRef.current);
  }, [fetchBatches, pagination]);

  const handleViewBatch = useCallback((batchId) => {
    navigate(`${PrefixPath.APP_PREFIX_PATH}/vms-invoice/batches/${batchId}`);
  }, [navigate]);

  const handleNewUpload = useCallback(() => {
    navigate(`${PrefixPath.APP_PREFIX_PATH}/vms-invoice/upload`);
  }, [navigate]);

  const columns = [
    {
      field: 'batchId',
      label: 'Batch ID',
      width: 110,
      pinned: 'left',
      sortable: false,
      primary: true,
      render: (id) => <Text strong>#{id}</Text>
    },
    {
      field: 'uploadDate',
      label: 'Upload Date',
      width: 180,
      sortable: false,
      render: (date) => CommonFunctions.formatUTCToLocal(date)
    },
    {
      field: 'uploadedBy',
      label: 'Uploaded By',
      flex: 1,
      minWidth: 200,
      sortable: false
    },
    {
      field: 'vendorId',
      label: 'Vendor ID',
      width: 150,
      sortable: false
    },
    {
      field: 'totalInvoices',
      label: 'Total Invoices',
      width: 130,
      align: 'center',
      sortable: false,
      render: (count) => <Tag color="blue">{count}</Tag>
    },
    {
      field: 'successfulUploads',
      label: 'Successful',
      width: 120,
      align: 'center',
      sortable: false,
      render: (count) => <Tag color="green">{count}</Tag>
    },
    {
      field: 'failedUploads',
      label: 'Failed',
      width: 100,
      align: 'center',
      sortable: false,
      render: (count) => <Tag color="red">{count}</Tag>
    },
    {

      field: 'notProcessedUploads',
      label: 'Not Processed',
      width: 140,
      align: 'center',
      sortable: false,
      render: (count) => <Tag>{count ?? 0}</Tag>,
      exportValue: (count) => count ?? 0
    },
    {
      field: 'status',
      label: 'Status',
      width: 150,
      sortable: false,
      render: (status, record) => {
        const displayStatus = getBatchDisplayStatus(record);
        return (
          <Tag icon={getStatusIcon(displayStatus)} color={getStatusColor(displayStatus)}>
            {getStatusLabel(displayStatus)}
          </Tag>
        );
      },
      exportValue: (status, record) => getStatusLabel(getBatchDisplayStatus(record))
    },
    {
      field: 'actions',
      label: 'Actions',
      width: 140,
      pinned: 'right',
      sortable: false,
      render: (_, record) => (
        <Button
          data-testid={`vmsinvoice-view-batch-btn-${record.batchId}`}
          type="link"
          icon={<FileTextOutlined />}
          onClick={() => handleViewBatch(record.batchId)}
        >
          View Details
        </Button>
      )
    }
  ];

  const expandedRowRender = (batch) => {
    const invoiceColumns = [
      {
        title: 'Invoice #',
        dataIndex: 'invoiceNumber',
        key: 'invoiceNumber',
        width: 150
      },
      {
        title: 'Property ID',
        dataIndex: 'propertyId',
        key: 'propertyId',
        width: 120
      },
      {
        title: 'Amount',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 120,
        render: (amount) => `$${parseFloat(amount).toFixed(2)}`
      },
      {
        title: 'Invoice Date',
        dataIndex: 'invoiceDate',
        key: 'invoiceDate',
        width: 130,
          render: (date) => Utils.formatDateUS(date)
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 160,
        render: (status) => (
          <Tag color={getStatusColor(status)} icon={getStatusIcon(status)} style={{ whiteSpace: 'nowrap' }}>
            {getStatusLabel(status)}
          </Tag>
        )
      },
      {
        title: 'Error',
        dataIndex: 'errorMessage',
        key: 'errorMessage',
        width: 260,
        ellipsis: true,
        render: (error) => error ? <Text type="danger" title={error}>{error}</Text> : '-'
      }
    ];

    return (
      <Spin spinning={loadingInvoices[batch.batchId] || false}>
        <Table
          columns={invoiceColumns}
          dataSource={expandedInvoices[batch.batchId] || []}
          rowKey="invoiceDetailId"
          pagination={false}
          size="small"
          tableLayout="fixed"
          scroll={{ x: 940 }}
          locale={{ emptyText: loadingInvoices[batch.batchId] ? 'Loading...' : 'No invoices' }}
        />
      </Spin>
    );
  };

  // Detail-panel content for the DataGridPro (reuses the invoices sub-table).
  // Depends on the invoice caches so the panel re-renders once data loads.
  const getDetailPanelContent = useCallback(
    ({ row }) => expandedRowRender(row),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expandedInvoices, loadingInvoices]
  );

  return (
    <div className="batch-list">
      <ContentCard>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button
              data-testid="vmsinvoice-upload-new-batch-btn"
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleNewUpload}
              size="large"
            >
              Upload New Batch
            </Button>
          </div>

          <Row gutter={[16, 16]}>
            <Col flex={1}>
              <Card>
                <Statistic
                  title="Total Batches"
                  value={allBatches.length}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card>
                <Statistic
                  title="Pending"
                  value={statistics.pendingBatches}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card>
                <Statistic
                  title="Completed"
                  value={statistics.completedBatches}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card>
                <Statistic
                  title="Partial Success"
                  value={statistics.partialSuccessBatches}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col flex={1}>
              <Card>
                <Statistic
                  title="Failed"
                  value={statistics.failedBatches}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Search Filters" size="small">
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Input
                  id="vmsinvoice-search-input"
                  placeholder="Search by batch ID or vendor ID"
                  prefix={<SearchOutlined />}
                  value={filters.invoiceNumber}
                  onChange={handleInvoiceNumberChange}
                  allowClear
                />
              </Col>
              <Col span={8}>
                <Select
                  data-testid="vmsinvoice-status-select"
                  placeholder="Filter by status"
                  value={filters.status || undefined}
                  onChange={handleStatusChange}
                  style={{ width: '100%' }}
                  allowClear
                >
                    <Option value="pre">Pending</Option>
                    <Option value="success">Completed</Option>
                    <Option value="failed">Failed</Option>
                    <Option value="validationfailed">Validation Failed</Option>
                    <Option value="error">Error</Option>
                </Select>
              </Col>
              <Col span={8}>
                <RangePicker
                  data-testid="vmsinvoice-date-range-picker"
                  style={{ width: '100%' }}
                  value={filters.dateRange}
                  onChange={handleDateChange}
                  placeholder={['Start Date', 'End Date']}
                />
              </Col>
              <Col span={24}>
                <Space>
                  <Button
                    data-testid="vmsinvoice-refresh-btn"
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                  >
                    Refresh
                  </Button>
                  <Button
                    data-testid="vmsinvoice-clear-filters-btn"
                    onClick={handleReset}
                  >
                    Clear Filters
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          <Spin spinning={loading}>
            <ResponsiveGrid
              columns={columns}
              rows={allBatches}
              getRowId={(row) => row.batchId}
              emptyText="No batches found."
              server={{
                page: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                onChange: (page, size) => fetchBatches(page, size, filtersRef.current)
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              getDetailPanelContent={getDetailPanelContent}
              detailPanelExpandedRowIds={expandedRowKeys}
              onDetailPanelExpandedRowIdsChange={handleDetailPanelChange}
            />
          </Spin>
        </Space>
      </ContentCard>
    </div>
  );
};

export default BatchList;
